import {
  alpha,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  styled,
  TextField,
} from '@mui/material';
import { DateRange } from '../../../data/models/simplified-workflow.model';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { lowerFirst } from 'lodash';
import * as monitoringSlice from '../../../data/monitoring.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import dateUtils from '../../../services/DateUtils';
import {
  GridCellParams,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { DataGridPro } from '../../tables/DataGridPro';
import { DateTime } from 'luxon';
import {
  WorkflowExecutionState,
  WorkflowExecutionStatus,
} from '../../../data/models/workflow-executions.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useNavigate } from 'react-router-dom';

interface WorkflowExecutionsProps {
  workflowUUID: string;
}

const PREFIX = 'WorkflowExecutions';

const classes = {
  container: `${PREFIX}-container`,
  filtersContainer: `${PREFIX}-filtersContainer`,
  select: `${PREFIX}-select`,
  chip: `${PREFIX}-chip`,
  activeChip: `${PREFIX}-activeChip`,
  inactiveChip: `${PREFIX}-inactiveChip`,
  grid: `${PREFIX}-grid`,
  abortButton: `${PREFIX}-abortButton`,
  loadMoreContainer: `${PREFIX}-loadMoreContainer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  [`& .${classes.filtersContainer}`]: {
    gap: theme.spacing(2),
  },
  [`& .${classes.select}`]: {
    width: 180,
  },
  [`& .${classes.chip}`]: {
    width: 74,
  },
  [`& .${classes.inactiveChip}`]: {
    backgroundColor: alpha(theme.palette.lightActionDisabled54p.main, 0.1),
    '& .MuiChip-label': {
      color: `${theme.palette.lightActionDisabled54p.main} !important`,
    },
  },
  [`& .${classes.activeChip}`]: {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  [`& .${classes.grid}`]: {
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: '10px',
    },
  },
  [`& .${classes.abortButton}`]: {
    height: 32,
  },
  [`& .${classes.loadMoreContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
  },
}));

export default function WorkflowExecutions({
  workflowUUID,
}: WorkflowExecutionsProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const allStates = t('workflows.allStates');
  const allStatuses = t('workflows.allStatuses');
  const validActiveStatuses = [
    WorkflowExecutionStatus.Running,
    WorkflowExecutionStatus.Waiting,
    WorkflowExecutionStatus.Held,
  ];
  const validInactiveStatuses = [
    WorkflowExecutionStatus.Succeeded,
    WorkflowExecutionStatus.Failed,
    WorkflowExecutionStatus.Canceled,
    WorkflowExecutionStatus.Aborted,
  ];
  const [selectedDateRange, setSelectedDateRange] = useState(DateRange.Today);
  const [selectedState, setSelectedState] = useState(allStates);
  const [selectedStatus, setSelectedStatus] = useState(allStatuses);
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [showAbortConfirm, setShowAbortConfirm] = useState<boolean>(false);
  const [abortExecutionId, setAbortExecutionId] = useState<string | undefined>(
    undefined,
  );
  const workflowHistory = useAppSelector(monitoringSlice.selectWorkflowHistory);
  const abortedWorkflowExecution = useAppSelector(
    monitoringSlice.selectAbortedWorkflowExecution,
  );
  const abortingWorkflowExecution = useAppSelector(
    monitoringSlice.selectAbortingWorkflowExecution,
  );
  const executions = workflowHistory?.workflowExecutions;

  const fetchWorkflowHistory = (useContinuation = false) => {
    const dateRange = getDateRangeAsISOString(selectedDateRange);

    const state =
      selectedState === allStates
        ? undefined
        : (selectedState as WorkflowExecutionState);
    const status =
      selectedStatus === allStatuses
        ? undefined
        : (selectedStatus as WorkflowExecutionStatus);
    const continuationToken = useContinuation
      ? workflowHistory?.continuationToken
      : undefined;
    dispatch(
      monitoringSlice.fetchWorkflowHistoryAsync(
        workflowUUID,
        dateRange.startDate,
        dateRange.endDate,
        state,
        status,
        continuationToken,
      ),
    );
  };

  useEffect(() => {
    if (!workflowUUID) {
      return;
    }
    fetchWorkflowHistory();
  }, [
    workflowUUID,
    selectedDateRange,
    selectedState,
    selectedStatus,
    abortedWorkflowExecution,
  ]);

  useEffect(() => {
    if (
      (selectedState === WorkflowExecutionState.Active &&
        !validActiveStatuses.includes(
          selectedStatus as WorkflowExecutionStatus,
        )) ||
      (selectedState === WorkflowExecutionState.Inactive &&
        !validInactiveStatuses.includes(
          selectedStatus as WorkflowExecutionStatus,
        ))
    ) {
      setSelectedStatus(allStatuses);
    }
  }, [selectedState]);

  const dateRanges = Object.values(DateRange).map((dateRange) => ({
    key: dateRange,
    value: t(`simplenotifications.${lowerFirst(dateRange)}`),
  }));

  const states = Object.values(WorkflowExecutionState).map((state) => ({
    key: state as string,
    value: t(`workflows.${state}`),
  }));
  states.unshift({ key: allStates, value: allStates });

  let validStatuses = Object.values(WorkflowExecutionStatus);
  if (selectedState === WorkflowExecutionState.Active) {
    validStatuses = validActiveStatuses;
  } else if (selectedState === WorkflowExecutionState.Inactive) {
    validStatuses = validInactiveStatuses;
  }
  const statuses = validStatuses.map((status) => ({
    key: status as string,
    value: t(`workflows.${status}`),
  }));
  statuses.unshift({ key: allStatuses, value: allStatuses });

  const handleDateRangeChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleAbortExecutionClick = (executionId: string) => {
    setShowAbortConfirm(true);
    setAbortExecutionId(executionId);
  };

  const handleAbortExecutionConfirm = () => {
    setShowAbortConfirm(false);
    if (abortExecutionId) {
      dispatch(monitoringSlice.abortWorkflowExecution(abortExecutionId));
    }
  };

  const handleLoadMoreClick = () => {
    fetchWorkflowHistory(true);
  };

  const handleExecutionClick = (params: GridRowParams) => {
    navigate(
      `/simple-notifications/execution/${params.row.workflowExecutionUUID}`,
    );
  };

  const renderState = (params: GridCellParams): React.ReactElement => {
    const className = `${params.row.state}Chip` as
      | 'activeChip'
      | 'inactiveChip';
    return (
      <Chip
        className={`${classes[className]} ${classes.chip}`}
        label={t(`workflows.${params.row.state}`)}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'modifiedDateTimeUTC',
      headerName: t('workflows.time'),
      sortable: false,
      minWidth: 180,
      valueFormatter: (params) =>
        dateUtils.formatDateTime(DateTime.fromISO(params.value)),
    },
    {
      field: 'workflowExecutionUUID',
      headerName: t('workflows.executionID'),
      sortable: false,
      minWidth: 300,
    },
    {
      field: 'workflowVersion',
      headerName: t('workflows.workflowVersion'),
      sortable: false,
      minWidth: 150,
    },
    {
      field: 'status',
      headerName: t('workflows.status'),
      sortable: false,
      valueFormatter: (params) => t(`workflows.${params.value}`),
      minWidth: 130,
    },
    {
      field: 'state',
      headerName: t('workflows.state'),
      sortable: false,
      renderCell: renderState,
      minWidth: 120,
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      renderCell: (params) => {
        // Show restart button on hover if execution failed
        if (
          params.row.workflowExecutionUUID === hoveredRow &&
          params.row.state === WorkflowExecutionState.Active
        ) {
          return (
            <Button
              className={classes.abortButton}
              color="primary"
              variant="contained"
              disabled={abortingWorkflowExecution}
              onClick={() =>
                handleAbortExecutionClick(params.row.workflowExecutionUUID)
              }>
              {t('workflows.abort')}
            </Button>
          );
        }
      },
    },
  ];

  return (
    <StyledBox>
      <Box className={classes.container}>
        <Stack direction="row" className={classes.filtersContainer}>
          <TextField
            select
            className={classes.select}
            label={t('workflows.executed')}
            variant="outlined"
            required={true}
            value={selectedDateRange}
            onChange={(evt) =>
              handleDateRangeChange(evt.target.value as DateRange)
            }>
            {dateRanges?.map((dateRange) => {
              return (
                <MenuItem value={dateRange.key} key={dateRange.key}>
                  {dateRange.value}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            className={classes.select}
            label={t('workflows.state')}
            variant="outlined"
            required={true}
            value={selectedState}
            onChange={(evt) => handleStateChange(evt.target.value)}>
            {states?.map((state) => {
              return (
                <MenuItem value={state.key} key={state.key}>
                  {state.value}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            className={classes.select}
            label={t('workflows.status')}
            variant="outlined"
            required={true}
            value={selectedStatus}
            onChange={(evt) => handleStatusChange(evt.target.value)}>
            {statuses?.map((status) => {
              return (
                <MenuItem value={status.key} key={status.key}>
                  {status.value}
                </MenuItem>
              );
            })}
          </TextField>
        </Stack>
        <DataGridPro
          className={classes.grid}
          disableColumnMenu={true}
          getRowId={(row) => row.workflowExecutionUUID}
          rows={executions ?? []}
          columns={columns}
          rowCount={executions?.length}
          pagination={true}
          autoHeight
          onRowClick={handleExecutionClick}
          slotProps={{
            row: {
              onMouseEnter: (event) => {
                const id = event.currentTarget.dataset.id;
                setHoveredRow(id);
              },
              onMouseLeave: () => {
                setHoveredRow(undefined);
              },
            },
          }}
        />
        {workflowHistory?.continuationToken && (
          <Box className={classes.loadMoreContainer}>
            <Button
              color="primary"
              variant="contained"
              onClick={handleLoadMoreClick}>
              {t(`workflows.loadMore`)}
            </Button>
          </Box>
        )}
      </Box>
      <ConfirmationDialog
        title={t('workflows.confirmAbortTitle')}
        message={t('workflows.confirmAbortMessage')}
        open={showAbortConfirm}
        positiveAction={handleAbortExecutionConfirm}
        negativeAction={() => setShowAbortConfirm(false)}
      />
    </StyledBox>
  );
}

function getDateRangeAsISOString(range: DateRange): {
  startDate: string;
  endDate: string;
} {
  const now = DateTime.now().startOf('day');
  let startDate: DateTime;
  let endDate: DateTime;

  switch (range) {
    case DateRange.Today:
      startDate = now;
      endDate = now;
      break;
    case DateRange.Yesterday:
      const yesterday = now.minus({ days: 1 });
      startDate = yesterday;
      endDate = yesterday;
      break;
    case DateRange.ThisWeek:
      startDate = now.startOf('week');
      endDate = now.endOf('week').startOf('day');
      break;
    case DateRange.ThisMonth:
      startDate = now.startOf('month');
      endDate = now.endOf('month').startOf('day');
      break;
  }

  return {
    startDate: startDate.toISO()!,
    endDate: endDate.toISO()!,
  };
}
